import { useEffect, useMemo } from "react";
import { BrowserAuthError, InteractionType } from "@azure/msal-browser";
import { useMsalAuthentication } from "@azure/msal-react";
import { Navigate } from "react-router-dom";
import { finishLogin, markLoginStarted } from "@/lib/courseLogin";
import { loginRequestFor } from "@/lib/previewTenant";

// useMsalAuthentication skips sign-in while any account is cached. This
// component only renders when none fits the route (a preview needs one from
// the internal tenant), so it matches no cached account.
const NO_CACHED_ACCOUNT = { homeAccountId: "sign-in-required" };

const isUserCancelled = (error: unknown) =>
  error instanceof BrowserAuthError && error.errorCode === "user_cancelled";

/**
 * Sends the visitor to the Microsoft sign-in screen and lets MSAL bring them
 * back to `returnTo` afterwards. Renders nothing meanwhile. Back from the
 * Microsoft screen shows Impressum instead of starting sign-in again.
 */
export const RedirectLogin = ({ returnTo }: { returnTo: string }) => {
  const request = useMemo(
    () => ({ ...loginRequestFor(returnTo), redirectStartPage: returnTo }),
    [returnTo]
  );
  const { error } = useMsalAuthentication(
    InteractionType.Redirect,
    request,
    NO_CACHED_ACCOUNT
  );
  const signInCancelled = isUserCancelled(error);

  const rememberWhereSignInStarted = () => {
    markLoginStarted(returnTo);
  };
  useEffect(rememberWhereSignInStarted, [returnTo]);

  const forgetCancelledSignIn = () => {
    if (!signInCancelled) return;
    finishLogin();
  };
  useEffect(forgetCancelledSignIn, [signInCancelled]);

  if (signInCancelled) return <Navigate to="/impressum" replace />;
  if (error) throw error;
  return null;
};
